/**
 * Loan / EMI — pure, deterministic amortized-payment math (rounded to cents).
 * Safe to render server-side.
 */

export interface LoanResult {
  /** Fixed monthly payment (EMI). */
  monthlyPayment: number;
  /** Total amount paid over the term. */
  totalPaid: number;
  /** Total interest paid (totalPaid − principal). */
  totalInterest: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Monthly payment for `principal` at `annualRatePercent` over `months`, using
 * the standard amortization formula. Zero-interest loans split evenly.
 */
export function compute(
  principal: number,
  annualRatePercent: number,
  months: number,
): LoanResult | null {
  if (
    ![principal, annualRatePercent, months].every((n) => Number.isFinite(n))
  ) {
    return null;
  }
  if (principal <= 0 || annualRatePercent < 0 || months < 1) return null;

  const n = Math.floor(months);
  const r = annualRatePercent / 100 / 12;

  let monthly: number;
  if (r === 0) {
    monthly = principal / n;
  } else {
    const pow = (1 + r) ** n;
    monthly = (principal * r * pow) / (pow - 1);
  }

  const totalPaid = monthly * n;
  return {
    monthlyPayment: round2(monthly),
    totalPaid: round2(totalPaid),
    totalInterest: round2(totalPaid - principal),
  };
}
